import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import PlacesAutocomplete from "react-places-autocomplete";
import { useTranslation } from "react-i18next";
import NotificationDialog from "../../HeaderContainer/Popup/NotificationDialog";
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useGetOptionCategoriesQuery } from '../../../features/details/detailsApiSlice';
import { selectCurrentLanguage } from '../../../features/auth/authSlice';
import { fireCategoriesTr } from '../../../utils/firebase.config';

const CATEGORY_AUTOCOMPLETE_STYLES = {
    container: base => ({ ...base, width: '100%', minWidth: 0 }),
    control: (base, state) => ({
        ...base,
        minHeight: 48,
        height: 48,
        border: 0,
        borderRadius: 0,
        background: 'transparent',
        boxShadow: state.isFocused ? 'inset 0 -2px 0 #2a41e8' : 'none',
        cursor: 'text'
    }),
    valueContainer: base => ({ ...base, height: 48, padding: '0 16px', overflow: 'hidden' }),
    input: base => ({ ...base, margin: 0, padding: 0, color: '#666', fontSize: 16 }),
    singleValue: base => ({ ...base, color: '#666', fontSize: 16 }),
    placeholder: base => ({ ...base, color: '#808080', fontSize: 16 }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: base => ({ ...base, padding: '8px 14px 8px 6px', color: '#808080' }),
    menu: base => ({ ...base, zIndex: 1000, marginTop: 0, borderRadius: '0 0 4px 4px', boxShadow: '0 4px 12px rgba(0,0,0,.14)' }),
    menuList: base => ({ ...base, maxHeight: 260, padding: 5 }),
    option: (base, state) => ({
        ...base,
        padding: '9px 12px',
        borderRadius: 4,
        color: state.isSelected || state.isFocused ? '#2a41e8' : '#666',
        background: state.isSelected || state.isFocused ? 'rgba(42,65,232,.08)' : '#fff',
        cursor: 'pointer'
    })
};

const SearchBar = ({isMapApiLoaded}) => {
    const { t } = useTranslation();
    const lang = useSelector(selectCurrentLanguage);
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [text, setText] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categoryTranslations, setCategoryTranslations] = useState(null);
    const { data: categoryData = [], isLoading: categoriesLoading, isFetching: categoriesFetching } = useGetOptionCategoriesQuery(undefined, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true
    });

    const [address, setAddress] = useState('');
    const [coordinates, setCoordinates] = useState({
        lat: null,
        lng: null
    });

    useEffect(() => {
        fireCategoriesTr(setCategoryTranslations).catch(error => {
            console.warn('Category translations are unavailable; using source names.', error);
        });
    }, []);

    const categoryOptions = useMemo(() => categoryData
        .filter(item => item.category !== 'Some category')
        .map(item => {
            const index = categoryTranslations?.input?.indexOf(item.category);
            return {
                value: Number(item.id),
                rawName: item.category,
                label: categoryTranslations?.translated?.[index]?.[lang] || item.category
            };
        })
        .sort((a, b) => a.label.localeCompare(b.label)), [categoryData, categoryTranslations, lang]);

    const handleSelect = async value => {
        const results = await geocodeByAddress(value);
        const latlng = await getLatLng(results[0]);

        setAddress(value);
        setCoordinates(latlng);
    };

    const onSearchBtnClick = () => {
        if (!selectedCategory) {
            setText('SelectACategory');
            setIsOpen(true);
            return;
        }

        if (address?.length > 0 && coordinates.lat && coordinates.lng) {
            navigate(`/jobs/${encodeURIComponent(selectedCategory.rawName.replaceAll('/', '-'))}`, {
                state: {
                    categoryId: selectedCategory.value,
                    category: selectedCategory.rawName,
                    pos: {...coordinates}
                }
            });
        } else {
            setText('EmptyAddress');
            setIsOpen(true);
        }
        
    };

    return <div className="row">
        <div className="col-md-12">
            <div className="intro-banner-search-form margin-top-95">

                {/* <!-- Search Field --> */}

                {/* <div className="intro-search-field with-autocomplete">
                    <label htmlFor="autocomplete-input" className="field-title ripple-effect">{t('Where')}</label>
                    <div className="input-with-icon">
                        <input id="autocomplete-input" type="text" placeholder={t('Place')} />
                        <i className="icon-material-outline-location-on"></i>
                    </div>
                </div>  */}

                {isMapApiLoaded && <PlacesAutocomplete
                    value={address}
                    onChange={setAddress}
                    onSelect={handleSelect}>
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                        <div className="intro-search-field with-autocomplete">
                            <label htmlFor="autocomplete-input" className="field-title ripple-effect">{t("Where")}</label>
                            <div className="input-with-icon">
                                <input id="autocomplete-input" type="text"
                                    {...getInputProps({
                                        placeholder: t('Place'),
                                        // className: 'location-search-input',
                                    })} />
                                <i className="icon-material-outline-location-on"></i>
                            </div>

                            <ul className="autocomplete-dropdown-container">
                                {loading && <li style={{ backgroundColor: '#ffffff', cursor: 'pointer', textAlign: 'center'}}>{t('Loading')}...</li>}
                                {suggestions.map((suggestion, i) => {
                                    const className = suggestion.active
                                        ? 'suggestion-item--active'
                                        : 'suggestion-item';
                                    // inline style for demonstration purpose
                                    const style = suggestion.active
                                        ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                                        : { backgroundColor: '#ffffff', cursor: 'pointer' };
                                    return (
                                        <li key={i}
                                            {...getSuggestionItemProps(suggestion, {
                                                className,
                                                style,
                                            })}
                                        >
                                            <span>{suggestion.description}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>)}
                </PlacesAutocomplete>}

                {/* <!-- Search Field --> */}
                <div className="intro-search-field">
                    <label htmlFor="intro-category" className="field-title ripple-effect">{t('WhatJobYouWant')}</label>
                    <Select
                        inputId="intro-category"
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        options={categoryOptions}
                        placeholder={t('SelectACategory')}
                        styles={CATEGORY_AUTOCOMPLETE_STYLES}
                        isSearchable
                        isClearable={false}
                        openMenuOnFocus
                        isLoading={categoriesLoading || categoriesFetching}
                        isDisabled={categoriesLoading || categoriesFetching}
                        noOptionsMessage={() => t('NoCategoriesAvailable')}
                        loadingMessage={() => `${t('Loading')}...`}
                    />
                </div>

                {/* <!-- Button --> */}
                <div className="intro-search-button">
                    <button className="button ripple-effect" 
                    onClick={onSearchBtnClick}>{t('Search')}</button>
                </div>
            </div>
        </div>

        <NotificationDialog type="warning" open={isOpen} onClose={() => setIsOpen(false)}>
            {t(text)}
        </NotificationDialog>
    </div>;
}

export default SearchBar;
